import * as React from 'react';
import { RouteComponentProps, useLocation } from 'react-router';
import styled from 'styled-components';
import { animated } from 'react-spring';
import { useMutation } from '@apollo/client';

import { SiteContext } from '../../../../utils/context/SiteContext';

import { Container } from '../../../layout/Container';

import { Heading } from '../../../ui/text/Heading';
import { Paragraph } from '../../../ui/text/Paragraph';

import { useFadeTransition } from '../../../../utils/hooks/useFadeTransition';
import { Seo } from '../../../../utils/seo/Index';

import { BaseHeroContent, Hero } from '../../../ui/hero/Index';

import { useForm } from '../../../../utils/hooks/useForm';
import { useFormTransition } from '../../../../utils/hooks/useFormTransition';

import { Section } from '../../../layout/Section';
import { Thanks } from '../../../layout/Thanks';

import { Input } from '../../../ui/form/Input';
import { FormError } from '../../../ui/form/FormError';
import { Form, FormRow } from '../../../ui/form/Form';
import { ButtonGroup } from '../../../ui/button/ButtonGroup';
import { Button } from '../../../ui/button/Button';
import { ButtonLink } from '../../../ui/link/ButtonLink';
import { createGTMEvent } from '../../../../utils/tracking/gtm';
import {
  GeneralValuation,
  GeneralValuationVariables
} from '../../../../__types__/generated/GeneralValuation';
import { GENERAL_VALUATION_MUTATION } from '../../../../../client/__graphql__/mutations/valuation';
import {
  LeadType,
  PageType
} from '../../../../__types__/generated/globalTypes';
import { useDigtectiveContext } from '../../../../__lib__/digtective/context/hooks/useDigtectiveContext';

const TipsRadVedSalg: React.FC = () => {
  const location = useLocation();
  const { routeConfig } = React.useContext(SiteContext);

  const { submitWithDigger } = useDigtectiveContext();

  const { fadeIn: pageFadeIn } = useFadeTransition({
    immidiate: true
  });

  const [posted, setPosted] = React.useState(false);
  const [loading, setLoading] = React.useState(false);

  const { fadeIn, fadeOut } = useFormTransition({
    posted
  });

  const [lead, { error }] = useMutation<
    GeneralValuation,
    GeneralValuationVariables
  >(GENERAL_VALUATION_MUTATION, {
    onCompleted: ({ lead }) => {
      setLoading(false);
      if (lead.valuation?.success) {
        window.scroll(0, 0);
        setPosted(true);

        // GTM tracking

        createGTMEvent({
          event: 'pmFormSubmission',
          gaEvent: 'RT_VALUATION',
          gaCategory: 'RT_VALUATION',
          gaAction: 'RT_SEND_VALUATION'
        });
      }
    }
  });

  const { fields, getFields, handleSubmit, handleChange } = useForm({
    fields: [
      {
        type: 'text',
        name: 'address',
        placeholder: 'Eks. Adresseveien 23',
        value: '',
        label: 'Adresse'
      },
      {
        type: 'text',
        name: 'zip',
        placeholder: 'Eks. 1234',
        value: '',
        label: 'Postnummer *',
        required: true
      },
      {
        type: 'tel',
        name: 'phone',
        placeholder: 'Eks. 00000000',
        value: '',
        label: 'Telefon *',
        labelAfter: '(Brukes til å kontakte deg)',
        required: true
      },
      {
        type: 'email',
        name: 'email',
        placeholder: 'Eks. ole@mail.no',
        value: '',
        label: 'E-post *',
        labelAfter: '(Brukes til å kontakte deg)',
        required: true
      }
    ],
    submit: () => {
      const preparedFields: any = getFields(fields);
      setLoading(true);
      submitWithDigger({ zip: preparedFields.zip }, ({ diggerId }) => {
        lead({
          variables: {
            input: {
              ...preparedFields,
              diggerId,
              referer: `${location.pathname}${location.search}`,
              leadType: LeadType.VERDIVURDERING,
              pageType: PageType.GENERELL
            }
          }
        });
      });
    }
  });

  return (
    <>
      <Seo
        title="Veiledning ved salg av eiendom - PrivatMegleren"
        description="PrivatMegleren deler sine beste råd ved salg av bolig, hytte og annen eiendom"
        url={location.pathname}
        image="https://cdn.reeltime.no/pm_assets/img/bakgrunner/pmfront.jpg"
        imageTitle="/gode-rad-ved-salg-bolig-eiendom"
      />
      <Hero
        imageUrl="https://cdn.reeltime.no/pm_assets/img/cbimage.jpg"
        opacity={0.4}
        height="600px"
        className="top-header-margin"
        alt="bilde"
      >
        <HeroContent style={pageFadeIn}>
          <Heading tag="h1" center={true}>
            Tips og råd ved salg av eiendom
          </Heading>
        </HeroContent>
      </Hero>
      <Container
        className="padding-top"
        style={{ ...pageFadeIn, paddingTop: '80px', paddingBottom: '80px' }}
      >
        <Heading tag="h2" center>
          De beste rådene ved salg av eiendom
        </Heading>
        <Paragraph center>
          Hva du gjør i forkant når du skal selge hus, leilighet eller
          fritidseiendom kan få stor betydning for resultatet. Meglers
          presentasjon av eiendommen din kan si mer enn tusen ord når det kommer
          til salg av eiendom.
        </Paragraph>
        <Heading tag="h2" center>
          Hvordan selge eiendom
        </Heading>
        <Paragraph center>
          PrivatMegleren har et sterkt ønske om å gjøre ditt salg av eiendom,
          din annonse, din visning og din budrunde til den beste i markedet.
          Våre meglere er eksperter på hvordan du selger leilighet, hus eller
          fritidseiendom, og for å få det beste salget er det viktig at
          eiendommen fremstår fra sin beste side ved salg.
        </Paragraph>
        <Paragraph center>
          Du trenger ikke nødvendigvis ha en kostbar styling i forbindelse med
          salg av eiendom. Noen timers konsulenthjelp kan være et rimeligere
          alternativ, som du kommer langt med når du skal selge hus, leilighet
          eller fritidseiendom. Hvordan selge eiendom best? Et godt råd fra
          megler er å se eiendommen gjennom en potensiell kjøpers øyne og ikke
          la deg farge av personlig tilknytning til huset eller leiligheten som
          er til salgs.
        </Paragraph>
        <Paragraph center>
          Utgangspunktet er at visningsgjestene kun ønsker å se det potensialet
          eiendommen har – og det er dette som skal fremheves når du selger
          huset, leiligheten eller fritidseiendommen. Eiendom er ikke lenger
          ditt hjem, men et produkt som skal selges.
        </Paragraph>
        <Paragraph center>
          Som meglere vet vi at ved salg av eiendom vil potensielle kjøpere ha
          vanskelig for å overse svært personlige gjenstander. Tanken er at
          potensielle kjøpere skal tenke «jeg kan se for meg å bo her», fremfor
          «jeg lurer på hvilke mennesker som bor her?».
        </Paragraph>
        <Heading tag="h2" center>
          Hva bør du fjerne før salg av eiendom?
        </Heading>
        <Paragraph center>
          Ta deg en runde i huset, leiligheten eller fritidseiendommen din, og
          tenk gjennom hva som kanskje bør fjernes av pynt og møbler. Med svært
          få unntak vil de fleste eiendommer se bedre ut med litt mindre møbler
          når du skal selge. Unngå møbler som blokkerer eller gjør det vanskelig
          for kjøpere å bevege seg rundt i eiendom, og behold kun det som viser
          rommenes formål.
        </Paragraph>
        <Paragraph center>
          Som din megler kan vi fortelle at dersom du har for mange møbler og
          for mye nips, risikerer du at eiendommen ikke kommer til sin rett ved
          eiendomssalg. Mindre møbler og færre pyntegjenstander skaper inntrykk
          av at rommene er større, store møbler og mange gjenstander gir motsatt
          romfølelse.
        </Paragraph>
        <Paragraph center>
          Et godt tips er å bruke speil på veggene for en forstørrende effekt,
          noe som kan være smart i en liten gang eller en smal stue.
        </Paragraph>
        <Paragraph center>
          Følelsen av hygge og velvære er allikevel viktig ved salg av eiendom.
          Små ting som å sette fram badesalt, lys eller lekre såper ved
          badekaret og kokebok sammen med en flaske rødvin på kjøkkenbenken,
          skaper god stemning. Du skal vise at her er det mulig å trives, på
          samme måte som du har gjort. Lag gjerne grupper med tre eller fem
          pyntegjenstander på bord eller benker, oddetall skaper mer dynamikk
          enn partall rent visuelt. Vi vet at all form for symmetri er
          fordelaktig for øyet som ser, enten det er puter i sofaen eller stoler
          rundt et spisebord.
        </Paragraph>
        <Paragraph center>
          Et godt tips er å se an sesongen du selger eiendommen i. Hvis du skal
          selge på vinteren, så kan skiene faktisk stå fremme - på den måten
          illustrerer du at det finnes gode ski- og turmuligheter i nærhet av
          eiendommen.
        </Paragraph>
        <Paragraph center>
          Meglere ser ofte at kjøperne gjerne åpner skap og skuffer. Derfor bør
          du gå over disse og sørge for at det ser organisert, ryddig og pent
          ut. Når du skal selge eiendom er det viktig at den framstår romslig,
          og da bør du unngå at ting ramler ut av skapene.
        </Paragraph>
        <Heading tag="h2" center>
          Tenk totalinntrykk når skal selge hus, leilighet eller fritidseiendom
        </Heading>
        <Paragraph center>
          Ikke glem en eventuell terrasse. Mal vegger, bruk høytrykkspyler og
          sett ut hagemøbler når årstidene tillater det. I sommersesongen kan du
          i tillegg pynte med blomster og eventuelle prydbusker. Området utenfor
          inngangsdøren må se innbydende ut for å gi et godt førsteinntrykk ved
          salg av eiendom. Mange potensielle kjøpere, særlig av
          familieiendommer, velger å kjøre forbi eiendommen før selve visningen.
          Et godt tips fra megler: Klipp gresset, luk ugress, trim hekker og
          plant blomster.
        </Paragraph>
        <Paragraph center>
          Husk at hage, trappeoppgang, garasje, kjeller og loft bidrar til
          helhetsinntrykket når du skal selge eiendom. Dette er noe av det som
          kan fortelle kjøper noe om hvordan eiendommen har blitt behandlet i
          tiden du har bodd der, og er viktig ved salg av eiendom. Er eiendommen
          en del av et sameie, bør du også ta for deg fellesarealer.
        </Paragraph>
        <Paragraph center>
          Det kan lønne seg å utbedre synlige skader på innvendige overflater
          når du skal selge eiendom. Har du for eksempel en dør eller et vindu
          som ikke kan lukkes skikkelig, burde du forsøke å få dette ordnet før
          salg av eiendom. Du kan også vurdere å male veggene i nøytrale farger.
          Har du overflater, lister eller andre detaljer i eiendommen som ikke
          passer med resten, bør du kanskje vurdere å gjøre noe med disse før du
          skal selge eiendom.
        </Paragraph>
        <Heading tag="h2" center>
          Generelle tips fra megler:
        </Heading>
        <OrderedList>
          <li>
            <b>Rengjør skikkelig og luft godt.</b> Få orden i bodene og på
            vaskerommet, her vil interessentene titte inn.
          </li>
          <li>
            <b>Rydd alle rom.</b> Visningsgjester vil lettere falle for et
            ryddig hjem. Når du tror du er ferdig å rydde – ta en runde til.
          </li>
          <li>
            For å<b>få det ”lyst og trivelig”</b>, bør du trekke opp alle
            persienner og gardiner for å slippe lyset inn. Tenn lys og bytt
            lyspærer som ikke virker. Har eiendommen peis, bør denne gjøres klar
            for opptenning, slik at den kan skape en god atmosfære på bilder og
            visning.
          </li>
          <li>
            Bestem deg for en gjennomgangsfarge og pynt opp med håndklær, puter
            og tekstiler i samme fargenyanse
          </li>
          <li>
            Sørg for å ha en <b>ryddig kjøkkenbenk!</b> Fjern alt som henger på
            kjøleskapdøren og alt som står på og under kjøkkeninnredningen. Sett
            gjerne frem stemningsskapende detaljer, som et fruktfat på
            kjøkkenbordet sammen med noen stearinlys. I vinduskarmene kan du
            sette urter eller andre grønne planter.
          </li>
          <li>
            Legg et lyst <b>sengeteppe</b> på sengen, dette gjør mulig visningen
            av soverommet mye mer innbydende og man slipper å re opp sengen hver
            gang megler ønsker en privatvisning. Luft også godt før visning og
            rydd vekk alle personlige eiendeler som klær, leker og
            pyntegjenstander (duftspray er ikke anbefalt).
          </li>
          <li>
            <b>Fjern personlige</b> ting og ta ut søppelet, skittentøyskurv,
            stellebord og lignende. Duftlys gjør seg alltid på badet, sammen med
            pene såpestykker, badesalt og rene håndklær i en farge som passer.
          </li>
          <li>
            Det kan også være praktisk å <b>varsle naboer</b> om visningen. Bor
            du i leilighet, be dem dempe musikk og ulyder mens visningen pågår.
            En god pris for deg kan i fremtiden komme dem til gode, om de selv
            skal selge.
          </li>
        </OrderedList>
        <Heading tag="h2" center>
          Klar for salg av eiendom
        </Heading>
        <Paragraph center>
          Still deg opp utenfor eiendommen. Ser det innbydende ut? Ønsker du å
          gå inn?
        </Paragraph>
        <Paragraph center>
          Still deg i hver døråpning i hele eiendommen og tenk at dette er slik
          hvert rom vil fremstå for alle interessenter ved salg av eiendom.
          Hvordan er møbler plassert, bør de plasseres på en annen måte? Henger
          gardinene jevnt? Hvordan er helhetsinntrykket av rommet, føles det
          riktig?
        </Paragraph>
        <Paragraph center>
          <i>Kilde: “Eiendomsboken”, Gyldendal Norsk Forlag AS</i>
        </Paragraph>
        <Paragraph center>
          Utgangspunktet for enhver som skal selge hus eller leilighet vil
          selvsagt variere, følgende bilder er kun ment som illustrasjon og for
          å gi ideer.
        </Paragraph>
        <ImageGrid>
          <Image
            caption="Enkelt – uten personlig preg."
            src="https://cdn.reeltime.no/pm_assets/content/tipsradvedsalg/03.jpeg"
          />
          <Image
            caption="Innbydende, men ikke overdrevet."
            src="https://cdn.reeltime.no/pm_assets/content/tipsradvedsalg/04.jpeg"
          />
          <Image
            caption="Her har jeg trivdes – det vil også du gjøre."
            src="https://cdn.reeltime.no/pm_assets/content/tipsradvedsalg/05.jpeg"
          />
          <Image
            caption="En ryddig gang gir inntrykk av god plass og skaper et godt førsteinntrykk."
            src="https://cdn.reeltime.no/pm_assets/content/tipsradvedsalg/06.jpeg"
          />
          <Image
            caption="En rød tråd (grønn) gir en helhet i bildet."
            src="https://cdn.reeltime.no/pm_assets/content/tipsradvedsalg/07.jpeg"
          />
          <Image
            caption="Sengeteppet gjør soverommet «ryddig» og forenkler visninger for selgeren."
            src="https://cdn.reeltime.no/pm_assets/content/tipsradvedsalg/08.jpeg"
          />
          <Image
            caption="Gjør utenomhus areal trivelig, være seg enebolig..."
            src="https://cdn.reeltime.no/pm_assets/content/tipsradvedsalg/09.jpeg"
          />
          <Image
            caption="...eller en leilighet."
            src="https://cdn.reeltime.no/pm_assets/content/tipsradvedsalg/10.jpeg"
          />
        </ImageGrid>
        <FormWrapper style={fadeOut}>
          <Section className="form-section" style={{ paddingBottom: '10px' }}>
            <Heading tag="h2" center={true}>
              Kontakt
            </Heading>
          </Section>
          <Form onSubmit={handleSubmit} noValidate>
            {error &&
              error.graphQLErrors.map((error, index) => (
                <FormError key={`error_${index}`} message={error.message} />
              ))}
            {fields.map((item: any, index: number) => {
              return (
                <FormRow key={`field_${index}`}>
                  <Input
                    type={item.type}
                    name={item.name}
                    label={item.label}
                    labelAfter={item.labelAfter}
                    placeholder={item.placeholder}
                    value={item.value}
                    onChange={handleChange}
                    error={item.error}
                  />
                </FormRow>
              );
            })}
            <ButtonGroup>
              <Button
                type="primary"
                colorTheme="gold"
                disabled={loading}
                loading={loading}
              >
                Send
              </Button>
              <ButtonLink
                as="a"
                href={routeConfig.personvern.url}
                className="privacy-link-mobile"
              >
                Personvernpolicy
              </ButtonLink>
            </ButtonGroup>
          </Form>
        </FormWrapper>
        <Thanks style={fadeIn}>
          <Section className="form-section">
            <Heading tag="h2" center={true}>
              Tusen takk
            </Heading>
          </Section>
        </Thanks>
      </Container>
    </>
  );
};

export default TipsRadVedSalg;

const FormWrapper = styled(animated.div)``;

const HeroContent = styled(animated.div)`
  ${BaseHeroContent}
  flex-flow: column;
  max-width: 960px;
`;

const OrderedList = styled.ol`
  li {
    margin-bottom: 10px;
  }
`;

const Image: React.FC<{ caption: string; src: string }> = ({
  caption,
  src
}) => {
  return (
    <StyledImageWrapper>
      <img src={src} alt="bilde-inspirasjon" />
      <Paragraph center>{caption}</Paragraph>
    </StyledImageWrapper>
  );
};

const StyledImageWrapper = styled.div`
  width: 100%;

  img {
    width: 100%;
  }

  p {
    margin-top: 20px;
  }
`;

const ImageGrid = styled.div`
  margin-top: 50px;
  display: grid;
  grid-template-columns: 1fr;
  grid-column-gap: 20px;
  grid-row-gap: 20px;

  @media all and (min-width: 960px) {
    grid-template-columns: 1fr 1fr;
  }
`;
